import type { AgeTransitionEvent, Card, ClientMessage, Decision, Identity, RoomSnapshot, ServerMessage } from '../shared/types';
import { renderRoom } from './views';
import { DrawInteraction } from './draw-interaction';
import { canConfirmSetback, selectionLimit, toggleSetbackCard } from './setback-selection';
import { downloadLifeDocument } from './life-document';
import { CardTextLayout } from './card-text-layout';
import { LostDrawerDock, setLostDrawerOpen } from './lost-drawer';
import { AgeTransition } from './age-transition';
import { AgeTransitionTracker } from './age-transition-tracker';
import { SeatDrag } from './seat-controls';
import { updateTabletopMarkup } from './tabletop-render';

interface SavedIdentity extends Identity { nickname: string }
const ACTIVE_KEY = 'life-cards:active:v1';
const SAVED_KEY = 'life-cards:saved:v1';
const app = document.querySelector<HTMLElement>('#app')!;
const chapterEvents=new AgeTransitionTracker();
const ageTransition=new AgeTransition(app,{covered:()=>render(),finished:()=>render()});
new CardTextLayout(app);
let identity: SavedIdentity | null = null;
let room: RoomSnapshot | null = null;
let cards = new Map<string, Card>();
let environment = '';
let socket: WebSocket | null = null;
let status: 'offline' | 'connecting' | 'online' = 'offline';
let initialSelection = new Set<string>();
let preview = new Set<string>();
let previewInFlight: string[] | null = null;
let error = '';
let busy = false;
let pendingConfirmation = false;
let pendingId: string | null = null;
let decision: Decision | null = null;
let viewedPlayerId: string | null = null;
let inspectedCardId: string | null = null;
const detailStates = new Map<string,boolean>();
let resetDetailsOnRender = false;
let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
let heartbeat: ReturnType<typeof setInterval> | undefined;
let lastResponse = Date.now();
let retry = 0;
let intentionalClose = false;
let connectionGeneration = 0;
let serverOffset = 0;
let drawTimer: ReturnType<typeof setTimeout> | undefined;
let drawUI: DrawInteraction | null = null;
let auxView:'history'|'lost'|'rules'|null=null;
let lostDrawerPlayerId:string|null=null;
let drawerTab:'lost'|'accepted'='lost';
const seatDrag=new SeatDrag(app,()=>({room,playerId:identity?.playerId ?? null,online:status==='online'}),(playerIds,expectedRevision)=>send({type:'reorder-players',playerIds,expectedRevision}));
const lostDock=new LostDrawerDock(app,(open,tab)=>{
  if(tab)drawerTab=tab;
  lostDrawerPlayerId=open?app.querySelector<HTMLElement>('.tabletop')?.dataset.tablePlayer ?? null:null;
  setLostDrawerOpen(app,open,drawerTab);
});
function serverNow(): number { return Date.now() + serverOffset; }
function scheduleDrawReveal(): void {
  clearTimeout(drawTimer);
  if (!room) return;
  const deadlines=room.players.flatMap(p=>{
    const record=p.stages[room!.stageIndex];
    return [record?.setbackDraw?.revealAt,record?.questionDraw?.revealAt].filter((at): at is number=>typeof at==='number' && at>serverNow());
  });
  if(deadlines.length) drawTimer=setTimeout(()=>{ render(); scheduleDrawReveal(); },Math.max(0,Math.min(...deadlines)-serverNow())+25);
}

const escape = (value: unknown): string => String(value).replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]!);
const bilingual = (zh: string, en: string): string => `${escape(zh)}<span class="en">${escape(en)}</span>`;
function storedIdentities(): SavedIdentity[] {
  try { return JSON.parse(localStorage.getItem(SAVED_KEY) ?? '[]') as SavedIdentity[]; } catch { return []; }
}
function remember(value: SavedIdentity): void {
  const saved = storedIdentities().filter(item => item.playerId !== value.playerId);
  localStorage.setItem(SAVED_KEY, JSON.stringify([value, ...saved].slice(0, 20)));
  sessionStorage.setItem(ACTIVE_KEY, JSON.stringify(value));
}
function render(): void {
  seatDrag.refresh();
  // The model already follows the official snapshot. Keep only the old DOM
  // for the first 420ms, until the chapter paper has covered it completely.
  if(ageTransition.holdingOldTable)return;
  const wasDrawBusy=!!drawUI && !['deck','choosing','settled'].includes(drawUI.state.phase);
  const visiblePlayer=room?.players.find(player=>player.id===viewedPlayerId)?.id ?? room?.turnPlayerId ?? identity?.playerId;
  const keepingDraw=!!drawUI?.root.isConnected && drawUI.root.dataset.drawKey===`${room?.code}:${room?.stageIndex}:${visiblePlayer}:${room?.phase}`;
  const oldTimeline=app.querySelector('.table-timeline');
  const oldScroll = keepingDraw?null:window.scrollY;
  const scrollPositions=keepingDraw?[]:[...app.querySelectorAll<HTMLElement>('[data-scroll-key]')].map(e=>({key:e.dataset.scrollKey!,left:e.scrollLeft,top:e.scrollTop}));
  const drawHand=drawUI?.root.querySelector<HTMLElement>('.draw-results');
  const handScroll=keepingDraw?null:drawHand?.scrollLeft ?? 0;
  if (!resetDetailsOnRender) for (const item of app.querySelectorAll<HTMLDetailsElement>('details[data-key]')) detailStates.set(item.dataset.key!,item.open);
  resetDetailsOnRender = false;
  const active = document.activeElement as HTMLElement | null;
  const focusCard = active?.dataset.card;
  const focusAction = active?.dataset.action;
  const focusPlayer = active?.dataset.player;
  document.body.classList.toggle('tabletop-mode',!!room && ['setback','questions','summary'].includes(room.phase));
  updateTabletopMarkup(app,`${error ? `<div class="notice error" role="alert">${escape(error)}<button data-action="dismiss" aria-label="关闭提示 / Dismiss">×</button></div>` : ''}${identity ? roomView() : landingView()}`);
  const drawHost=app.querySelector<HTMLElement>('[data-draw-host],.draw-interaction');
  if(drawHost && room && identity) {
    const actor=room.players.find(player=>player.id===drawHost.dataset.drawPlayer)!;
    const kind=drawHost.dataset.drawKind as 'setback'|'questions';
    const context={key:drawHost.dataset.drawHost ?? drawHost.dataset.drawKey!,kind,deckSize:room.drawPool?.[kind==='setback'?'setbacks':'questions'],own:actor.id===identity.playerId,canAct:actor.id===identity.playerId && room.turnPlayerId===actor.id && status==='online',pending:pendingConfirmation,record:actor.stages[room.stageIndex]!,cards,now:serverNow,visual:actor.id===room.turnPlayerId ? room.turnPreview.draw : null};
    if(drawUI?.root.dataset.drawKey!==context.key) { drawUI?.dispose();drawUI=new DrawInteraction(context,requestDraw,render,visual=>{if(room?.turnPlayerId===identity?.playerId && status==='online')send({type:'draw-visual',visual});}); }
    drawUI.root.dataset.drawPlayer=actor.id;
    if(drawHost!==drawUI.root)drawHost.replaceWith(drawUI.root);
    drawUI.sync(context);
    if(drawHand?.isConnected && handScroll!==null)drawHand.scrollLeft=handScroll;
  } else { drawUI?.dispose();drawUI=null; }
  for (const item of app.querySelectorAll<HTMLDetailsElement>('details[data-key]')) if (detailStates.has(item.dataset.key!)) item.open = detailStates.get(item.dataset.key!)!;
  for(const position of scrollPositions){const e=app.querySelector<HTMLElement>(`[data-scroll-key="${CSS.escape(position.key)}"]`);if(e){e.scrollLeft=position.left;e.scrollTop=position.top;}}
  const timeline=app.querySelector<HTMLElement>('.table-timeline');
  const activeAge=timeline?.querySelector<HTMLElement>('.active');
  if(timeline && activeAge && timeline!==oldTimeline)timeline.scrollLeft=activeAge.offsetLeft-timeline.offsetLeft-(timeline.clientWidth-activeAge.offsetWidth)/2;
  if (inspectedCardId || auxView) app.querySelector<HTMLDialogElement>('dialog')?.showModal();
  if (focusCard && !active?.isConnected) app.querySelector<HTMLElement>(`[data-card="${CSS.escape(focusCard)}"]`)?.focus({ preventScroll: true });
  else if (focusAction && !active?.isConnected) app.querySelector<HTMLElement>(`[data-action="${CSS.escape(focusAction)}"]${focusPlayer ? `[data-player="${CSS.escape(focusPlayer)}"]` : ''}`)?.focus({ preventScroll: true });
  if(oldScroll!==null)window.scrollTo({ top: oldScroll, behavior: 'instant' });
  lostDock.refresh();seatDrag.refresh();
  const isDrawBusy=!!drawUI && !['deck','choosing','settled'].includes(drawUI.state.phase);
  if(wasDrawBusy!==isDrawBusy)queueMicrotask(render);
}
function landingView(): string {
  const suggested = new URLSearchParams(location.search).get('room') ?? '';
  const saved = storedIdentities();
  return `<section class="welcome"><div class="hero-copy"><p class="eyebrow">一桌相聚，各自成篇 / ONE TABLE, MANY STORIES</p><h1>你会留下<br><em>哪些底牌？</em></h1><p class="hero-en">What will you hold on to?</p><p class="intro">每一张牌，都是人生的一种可能。<br>和朋友坐在同一张桌前，看见彼此的选择。<span class="en">Every card is a possibility. Share a table and see each other's choices.</span></p><div class="paper-stack" aria-hidden="true"><div>人生<span>LIFE</span><b>↗</b></div><div>底牌<span>CARDS</span><b>◇</b></div><div>同行<span>TOGETHER</span><b>✳</b></div></div></div>
  <div class="entry-panel"><p class="eyebrow">坐下来，开始 / TAKE A SEAT</p><h2>加入一场人生旅程<span class="en">A life journey with friends</span></h2><form id="entry-form"><label for="nickname">你的昵称 / Your nickname</label><input id="nickname" name="nickname" maxlength="24" autocomplete="nickname" placeholder="比如：CC / e.g. CC" required><button type="submit" name="intent" value="create" ${busy ? 'disabled' : ''}>${bilingual(busy ? '请稍候…' : '创建房间', busy ? 'Please wait…' : 'Create a room')}<span class="arrow">↗</span></button><div class="divider">或加入朋友 / OR JOIN FRIENDS</div><label for="room-code">六位房间码 / 6-character room code</label><input id="room-code" name="roomCode" value="${escape(suggested.toUpperCase())}" maxlength="6" autocomplete="off" autocapitalize="characters" spellcheck="false" placeholder="ABC234"><button class="secondary" type="submit" name="intent" value="join" ${busy ? 'disabled' : ''}>${bilingual('加入房间', 'Join room')}</button></form>
  ${saved.length ? `<div class="saved"><h3>恢复已有玩家 / Restore a player</h3>${saved.map((item, index) => `<button class="restore" data-action="restore" data-index="${index}">${escape(item.nickname)}<small>${escape(item.roomCode)} · 恢复 / Resume</small></button>`).join('')}</div>` : ''}
  </div></section>`;
}
function roomView(): string {
  return renderRoom({ room, playerId: identity!.playerId, cards, status, environment, initialSelection, preview, decision, pending: pendingConfirmation, viewedPlayerId, inspectedCardId, auxView, drawerTab, lostDrawerOpen:lostDrawerPlayerId===(viewedPlayerId ?? room?.turnPlayerId ?? identity?.playerId), now: serverNow(), drawBusy: !!drawUI && !['deck','choosing','settled'].includes(drawUI.state.phase), drawLayoutReady: drawUI?.root.dataset.drawLayout==='settled', questionRedrawCount:drawUI?.selectedQuestionIndices().length ?? 0 });
}
function requestDraw(kind:'setback'|'questions',questionIndex:number): string {
  pendingId=crypto.randomUUID();pendingConfirmation=true;
  send({type:kind==='setback'?'draw-setback':'draw-questions',requestId:pendingId,expectedRevision:room!.revision,...(kind==='questions'?{questionIndex}:{})});
  render();return pendingId!;
}
async function api(path: string, body: unknown, token?: string): Promise<Record<string, unknown>> {
  const response = await fetch(path, { method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify(body) });
  const result = await response.json() as Record<string, unknown>;
  if (!response.ok) throw new Error(`${result.messageZh ?? '请求失败'} / ${result.messageEn ?? 'Request failed'}`);
  return result;
}
function send(message: ClientMessage): void {
  if (status !== 'online' || socket?.readyState !== WebSocket.OPEN) { error = '连接尚未恢复，请稍候。 / Wait for the connection to return.'; render(); return; }
  socket.send(JSON.stringify(message));
}
function publishPreview(): void {
  // Keep rapid toggles locally and send the newest selection after the prior echo.
  // Other players' broadcasts must not roll back a newer local choice.
  if (previewInFlight || pendingConfirmation) return;
  previewInFlight = [...preview];
  send({ type: 'preview', cardIds: previewInFlight });
}
function clearTimers(): void { clearTimeout(reconnectTimer); clearInterval(heartbeat); clearTimeout(drawTimer); }
function playAgeTransition(event:AgeTransitionEvent):void {
  seatDrag.cancel();
  inspectedCardId=null;auxView=null;
  for(const dialog of app.querySelectorAll<HTMLDialogElement>('dialog[open]'))dialog.close();
  ageTransition.play(event);
}
function connect(): void {
  seatDrag.cancel();ageTransition.cancel();chapterEvents.reset();
  if (!identity) return;
  clearTimers();
  drawUI?.dispose();drawUI=null;
  const generation = ++connectionGeneration;
  intentionalClose = false;
  status = 'connecting';
  preview.clear();
  previewInFlight = null;
  pendingConfirmation = false;
  pendingId = null; decision = null;
  render();
  const url = new URL(`/api/rooms/${identity.roomCode}/ws`, location.origin);
  url.protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
  const ws = new WebSocket(url);
  socket = ws;
  const current = () => generation === connectionGeneration && socket === ws;
  ws.addEventListener('open', () => {
    if (!current() || !identity) return;
    lastResponse = Date.now();
    ws.send(JSON.stringify({ type: 'auth', playerId: identity.playerId, token: identity.token }));
    heartbeat = setInterval(() => {
      if (!current()) return;
      if (Date.now() - lastResponse > 45_000) ws.close(4000, 'Heartbeat timeout');
      else if (ws.readyState === WebSocket.OPEN) ws.send('ping');
    }, 15_000);
  });
  ws.addEventListener('message', event => {
    if (!current()) return;
    lastResponse = Date.now();
    if (event.data === 'pong') return;
    const message = JSON.parse(event.data as string) as ServerMessage;
    if(message.type==='age-transition'){
      if(status==='online' && chapterEvents.accept(message.transition,serverNow())){
        playAgeTransition(message.transition);
      }
      return;
    }
    let followTurnView=false;
    if (message.type === 'snapshot') {
      const previous = room;
      room = message.room;
      const opening=status==='online'?chapterEvents.openingFromSnapshot(room):null;
      chapterEvents.observe(room);
      if(opening)playAgeTransition(opening);
      serverOffset = room.serverNow - Date.now();
      if(viewedPlayerId && !room.players.some(player=>player.id===viewedPlayerId)){
        viewedPlayerId=room.turnPlayerId ?? identity!.playerId;inspectedCardId=null;lostDrawerPlayerId=null;
        auxView=null;
      }
      const turnChanged=!!previous && previous.turnPlayerId !== room.turnPlayerId;
      if(room.turnPlayerId && (turnChanged || !previous || previous.phase!==room.phase || previous.stageIndex!==room.stageIndex)) {
        viewedPlayerId=room.turnPlayerId; inspectedCardId=null;
        auxView=null;lostDrawerPlayerId=null;
        detailStates.clear(); resetDetailsOnRender=true;
        followTurnView=true;
      }
      if(turnChanged) { decision=null; preview.clear(); previewInFlight=null; }
      if (!previous || previous.phase !== room.phase || previous.stageIndex !== room.stageIndex) decision = null;
      if (previous && (previous.phase !== room.phase || previous.stageIndex !== room.stageIndex)) { detailStates.clear(); resetDetailsOnRender = true; }
      cards = new Map(message.cards.map(card => [card.id, card]));
      environment = message.environment;
      const own = room.players.find(player => player.id === identity?.playerId);
      const officialPreview = own?.previewSelection ?? [];
      if (!previous || previous.phase !== room.phase || previous.stageIndex !== room.stageIndex || own?.stages[room.stageIndex]?.decision) {
        previewInFlight = null; preview = new Set(officialPreview);
      } else if (previewInFlight) {
        if (JSON.stringify(previewInFlight) === JSON.stringify(officialPreview)) {
          previewInFlight = null;
          if (JSON.stringify([...preview]) !== JSON.stringify(officialPreview)) publishPreview();
        }
      } else preview = new Set(officialPreview);
      status = 'online'; retry = 0;
      if (!pendingId && room.players.find(player => player.id === identity?.playerId)?.initialConfirmed) pendingConfirmation = false;
      scheduleDrawReveal();
    } else if (message.type === 'error') {
      seatDrag.cancel();
      drawUI?.failPending();
      error = `${message.messageZh} / ${message.messageEn}`;
      pendingConfirmation = false;
      pendingId = null;
      previewInFlight = null;
    } else if (message.type === 'ack' && message.requestId === pendingId) { pendingConfirmation = false; pendingId = null; }
    render();
    if(followTurnView && !document.body.classList.contains('tabletop-mode')) document.querySelector('.draw-interaction')?.scrollIntoView({block:'start',behavior:'instant'});
  });
  ws.addEventListener('close', event => {
    if (!current()) return;
    if(event.code===4004){
      const removedId=identity?.playerId;
      localStorage.setItem(SAVED_KEY,JSON.stringify(storedIdentities().filter(saved=>saved.playerId!==removedId)));
      leave();error='房主已将你移出房间，卡牌与记录已清除。 / The host removed you from this room. Your game record has been cleared.';render();return;
    }
    ageTransition.cancel();chapterEvents.reset();
    clearTimers(); status = 'offline'; preview.clear(); previewInFlight = null; pendingConfirmation = false; pendingId = null; decision = null;
    if (room) room.players = room.players.map(player => player.id === identity?.playerId ? { ...player, connected: false, previewSelection: [] } : player);
    if (event.code === 4001) error = '此玩家身份已在另一个窗口打开。 / This player is open in another window.';
    if (event.code === 4003) error = '身份验证失败。请返回首页恢复保存的身份。 / Authentication failed. Return home to restore an identity.';
    render();
    if (!intentionalClose && event.code !== 4001 && event.code !== 4003) reconnectTimer = setTimeout(connect, Math.min(1000 * 2 ** retry++, 15_000) + Math.random() * 300);
  });
  ws.addEventListener('error', () => { /* The close event drives reconnect, including failed handshakes. */ });
}
async function resume(saved: SavedIdentity): Promise<void> {
  await api(`/api/rooms/${saved.roomCode}/resume`, { playerId: saved.playerId }, saved.token);
  identity = saved; room = null; auxView=null;error = ''; initialSelection.clear(); remember(saved); connect();
  viewedPlayerId = saved.playerId;
}
function leave(): void {
  ageTransition.cancel();chapterEvents.reset();
  intentionalClose = true; clearTimers(); ++connectionGeneration;
  socket?.close(1000, 'Returning home'); socket = null;
  identity = null; room = null; status = 'offline'; initialSelection.clear(); preview.clear(); pendingConfirmation = false;
  previewInFlight = null; pendingId = null; decision = null;
  viewedPlayerId = null; inspectedCardId = null;auxView=null; detailStates.clear(); resetDetailsOnRender = true;
  sessionStorage.removeItem(ACTIVE_KEY); render();
}
app.addEventListener('submit', async event => {
  event.preventDefault();
  if (busy) return;
  const form = event.target as HTMLFormElement;
  const submitter = (event as SubmitEvent).submitter as HTMLButtonElement;
  const fields = new FormData(form);
  const nickname = String(fields.get('nickname') ?? '').trim();
  const code = String(fields.get('roomCode') ?? '').trim().toUpperCase();
  if (submitter.value === 'join' && !/^[A-HJ-NP-Z2-9]{6}$/.test(code)) { error = '请输入正确的六位房间码。 / Enter a valid 6-character room code.'; render(); return; }
  busy = true; error = '';
  submitter.disabled = true;
  try {
    const result = await api(submitter.value === 'create' ? '/api/rooms' : `/api/rooms/${code}/join`, { nickname });
    identity = { ...(result as unknown as Identity), nickname };
    viewedPlayerId = identity.playerId; inspectedCardId = null; detailStates.clear(); resetDetailsOnRender = true;
    remember(identity); initialSelection.clear(); room = null; connect();
  } catch (err) { error = (err as Error).message; }
  finally { busy = false; render(); }
});
app.addEventListener('click', async event => {
  const target = (event.target as HTMLElement).closest<HTMLElement>('button, .detail-card-face[data-action="close-card"]');
  if (!target || target.matches(':disabled')) return;
  error = '';
  try {
    const id = target.dataset.card;
    if (id && room) {
      if (room.phase === 'initial-selection') {
        if (initialSelection.has(id)) initialSelection.delete(id);
        else if (initialSelection.size < 12) initialSelection.add(id);
        else error = '最多选择 12 张。 / Select at most 12 cards.';
      } else if (room.phase === 'setback') {
        if (!preview.has(id) && preview.size >= selectionLimit(decision)) { error = decision === 'accept' ? '接受时最多选择 1 张。 / Select at most one card when accepting.' : '最多选择 2 张。 / Select at most two cards.'; render(); return; }
        preview = toggleSetbackCard(preview, id, decision);
        publishPreview();
      }
      render(); return;
    }
    switch (target.dataset.action) {
      case 'request-review': {
        if(room && identity && (!viewedPlayerId || viewedPlayerId===identity.playerId))send({type:'request-review',playerId:identity.playerId,language:target.dataset.language==='en'?'en':'zh-CN'});
        break;
      }
      case 'kick-player': {
        const player=room?.players.find(p=>p.id===target.dataset.player);
        if(!room || room.hostId!==identity?.playerId || !player || player.id===room.hostId)break;
        if(confirm(`移出 ${player.nickname} 并清除其本局记录、收回卡牌？\nRemove ${player.nickname}, clear their game record and return their cards?`))send({type:'kick-player',playerId:player.id,expectedRevision:room.revision});
        break;
      }
      case 'dismiss': render(); break;
      case 'view-player': viewedPlayerId = target.dataset.player!; auxView=null;lostDrawerPlayerId=null;render();break;
      case 'view-self': viewedPlayerId = identity!.playerId; render();break;
      case 'view-turn': viewedPlayerId = room!.turnPlayerId;auxView=null;lostDrawerPlayerId=null; render();break;
      case 'show-history':auxView='history';render();break;
      case 'show-lost':auxView='lost';render();break;
      case 'show-rules':auxView='rules';render();break;
      case 'close-aux':auxView=null;render();break;
      case 'inspect-card': {
        const value=cards.get(target.dataset.inspect!);
        if(value) { auxView=null;inspectedCardId=value.id; render(); }
        break;
      }
      case 'close-card': inspectedCardId = null; render(); break;
      case 'restore': await resume(storedIdentities()[Number(target.dataset.index)]!); break;
      case 'leave': leave(); break;
      case 'start': send({ type: 'start' }); break;
      case 'confirm-initial': send({ type: 'confirm-initial', cardIds: [...initialSelection] }); pendingConfirmation = true; render(); break;
      case 'cancel': preview.clear(); publishPreview(); render(); break;
      case 'accept': decision = 'accept';if(preview.size>1){preview.clear();publishPreview();}send({type:'preview-decision',decision}); render(); break;
      case 'reject': decision = 'reject';send({type:'preview-decision',decision}); render(); break;
      case 'confirm-setback':
      case 'complete-questions':
      case 'redraw-questions':
      case 'end-turn':
      case 'next-stage': {
        const action = target.dataset.action;
        if (action === 'confirm-setback' && !canConfirmSetback(decision, preview.size)) {
          error = '接受请选择 0–1 张；不接受请选择 2 张。 / Accept: select 0–1 cards. Reject: select exactly 2.'; render(); break;
        }
        pendingId = crypto.randomUUID();
        if (action === 'confirm-setback') send({ type: action, requestId: pendingId, expectedRevision: room!.revision, decision: decision!, cardIds: [...preview] });
        else if(action==='redraw-questions') {
          const indices=drawUI?.selectedQuestionIndices() ?? [];
          if(!indices.length){pendingId=null;break;}
          send({type:action,requestId:pendingId,expectedRevision:room!.revision,indices});
        }
        else send({ type: action, requestId: pendingId, expectedRevision: room!.revision });
        pendingConfirmation = true; render(); break;
      }
      case 'copy': {
        const url = new URL('/', location.origin); url.searchParams.set('room', room!.code);
        await navigator.clipboard.writeText(url.href);
        target.textContent = '已复制 / Copied'; break;
      }
      case 'export': {
        if(!room)break;
        const snapshot=room;
        const player=snapshot.players.find(p=>p.id===(viewedPlayerId ?? snapshot.turnPlayerId ?? identity?.playerId));
        if(player)downloadLifeDocument(snapshot,player,cards);
        break;
      }
    }
  } catch (err) { error = (err as Error).message; render(); }
});
app.addEventListener('cancel', event => { event.preventDefault(); inspectedCardId = null;auxView=null; render(); },true);
app.addEventListener('keydown', event => {
  if(event.key==='Escape' && lostDrawerPlayerId && !inspectedCardId && !auxView){lostDrawerPlayerId=null;setLostDrawerOpen(app,false);return;}
  if ((event.key==='Enter' || event.key===' ') && (event.target as HTMLElement).closest('.detail-card-face[data-action="close-card"]')) {
    event.preventDefault(); inspectedCardId=null;render();
  }
});
window.addEventListener('online', () => { if (identity && status !== 'online' && !intentionalClose) { socket?.close(); connect(); } });
render();
try {
  const active = sessionStorage.getItem(ACTIVE_KEY);
  if (active) void resume(JSON.parse(active) as SavedIdentity).catch(err => { error = (err as Error).message; sessionStorage.removeItem(ACTIVE_KEY); render(); });
} catch { sessionStorage.removeItem(ACTIVE_KEY); }
